import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { loadStripe, type Stripe as StripeJs } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  fetchAvailability,
  fetchBooking,
  fetchBookingByNumber,
  rescheduleBooking,
  rescheduleMultiBooking,
  resendTicketEmail,
  startBalancePayment,
  ticketIcsUrl,
  ticketPdfUrl,
  type BalancePaymentSession,
  type NewSlotInput,
} from './api';
import type { AvailabilitySlot, ManagePayload } from './types';
import {
  VENUE_TZ,
  formatMoney,
  mapsUrlFor,
  rescheduleState,
  todayVenueString,
  venueDate,
  venueDateTime,
  venueDayString,
  venueTime,
} from './lib/format';

type Mode = 'view' | 'reschedule' | 'pay';

// Uitkomst van een betaalpoging (inline of via redirect-terugkeer) — stuurt
// de banner boven de boekingssamenvatting.
type PayReturn = 'succeeded' | 'processing' | 'failed' | null;

function getToken(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('t');
}

// Landingsstatus na de uitschrijflink uit onze e-mails. De edge function
// booking-unsubscribe verwerkt de opt-out en stuurt de bezoeker hierheen met
// ?unsubscribed=ok|invalid|error&lang=nl|en (de Supabase functions-gateway
// serveert zelf geen HTML). Geen token nodig: de pagina toont alleen de
// uitkomst.
type UnsubscribedStatus = 'ok' | 'invalid' | 'error';
type UnsubscribedState = { status: UnsubscribedStatus; lang: 'nl' | 'en' };

function readUnsubscribed(): UnsubscribedState | null {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('unsubscribed');
  if (raw !== 'ok' && raw !== 'invalid' && raw !== 'error') return null;
  return { status: raw, lang: params.get('lang') === 'en' ? 'en' : 'nl' };
}

const UNSUBSCRIBED_COPY = {
  nl: {
    title: 'Uitgeschreven',
    ok: [
      'Je ontvangt geen nieuwsbrieven of aanbiedingen meer van Neoseum op dit e-mailadres.',
      'Bevestigingen en herinneringen voor tickets die je boekt blijf je gewoon ontvangen.',
    ],
    invalid: [
      'Deze uitschrijflink is ongeldig. Mail ons op bookings@neoseum.com en we regelen het voor je.',
    ],
    error: [
      'Er ging iets mis. Probeer het later opnieuw of mail bookings@neoseum.com.',
    ],
  },
  en: {
    title: 'Unsubscribed',
    ok: [
      'You will no longer receive newsletters or offers from Neoseum at this email address.',
      'Confirmations and reminders for tickets you book will still arrive as usual.',
    ],
    invalid: [
      'This unsubscribe link is invalid. Email us at bookings@neoseum.com and we will take care of it.',
    ],
    error: [
      'Something went wrong. Please try again later or email bookings@neoseum.com.',
    ],
  },
} as const;

function UnsubscribedCard({ status, lang }: UnsubscribedState) {
  const c = UNSUBSCRIBED_COPY[lang];
  return (
    <section className={status === 'ok' ? 'card' : 'card error-card'}>
      <h2>{c.title}</h2>
      {c[status].map((line) => <p key={line}>{line}</p>)}
    </section>
  );
}

// ?pay=1 (uit de betaallink-e-mail) → open de betaalflow direct.
function readPayIntent(): boolean {
  return new URLSearchParams(window.location.search).get('pay') === '1';
}

// Redirect-terugkeer van Stripe (iDEAL etc.): lees redirect_status en strip
// de Stripe-params uit de URL zodat een refresh de banner niet opnieuw
// triggert.
function readPaymentReturn(): PayReturn {
  const params = new URLSearchParams(window.location.search);
  const rs = params.get('redirect_status');
  if (!rs) return null;
  params.delete('redirect_status');
  params.delete('payment_intent');
  params.delete('payment_intent_client_secret');
  const qs = params.toString();
  window.history.replaceState(
    {},
    '',
    `${window.location.pathname}${qs ? `?${qs}` : ''}`,
  );
  if (rs === 'succeeded') return 'succeeded';
  if (rs === 'processing') return 'processing';
  return 'failed';
}

export function App() {
  const [token, setToken] = useState<string | null>(() => getToken());
  const [unsubscribed] = useState<UnsubscribedState | null>(() => readUnsubscribed());
  const [payload, setPayload] = useState<ManagePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('view');
  // Betaal-terugkeer (redirect-methodes) wordt éénmalig uit de URL gelezen;
  // inline (kaart)betalingen zetten dezelfde state via onPaid.
  const [payReturn, setPayReturn] = useState<PayReturn>(() => readPaymentReturn());
  const [payIntent] = useState<boolean>(() => readPayIntent());
  const autoOpenedPay = useRef(false);
  const settlePolls = useRef(0);

  const reload = useCallback(async () => {
    // No token → the lookup form renders instead (recovery entry via
    // booking number + email, e.g. from the Stripe receipt).
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const res = await fetchBooking(token);
    setLoading(false);
    if (res.ok) {
      setPayload(res.data);
    } else {
      setError(res.error.message ?? res.error.error ?? 'Onbekende fout');
    }
  }, [token]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // ?pay=1 uit de betaallink-e-mail: open de betaalflow zodra de payload er
  // is en er daadwerkelijk iets open staat. Eénmalig, zodat "Terug" werkt.
  useEffect(() => {
    if (!payIntent || autoOpenedPay.current) return;
    if (payload?.payment?.payable && mode === 'view') {
      autoOpenedPay.current = true;
      setMode('pay');
    }
  }, [payIntent, payload, mode]);

  // Na een (mogelijk) geslaagde betaling loopt de webhook enkele seconden
  // achter op de redirect — poll de payload tot de balans vereffend is.
  useEffect(() => {
    if (payReturn !== 'succeeded' && payReturn !== 'processing') return;
    if (!payload?.payment?.payable) return; // vereffend (of blok ontbreekt)
    if (settlePolls.current >= 10) return;
    settlePolls.current += 1;
    const t = setTimeout(() => {
      void reload();
    }, 2000);
    return () => clearTimeout(t);
  }, [payReturn, payload, reload]);

  if (unsubscribed) {
    return <Shell><UnsubscribedCard {...unsubscribed} /></Shell>;
  }
  if (loading && !payload) {
    return <Shell><p className="muted">Laden…</p></Shell>;
  }
  if (!token) {
    return (
      <Shell>
        <LookupView
          onFound={(p) => {
            setPayload(p);
            if (p.manage_token) setToken(p.manage_token);
          }}
        />
      </Shell>
    );
  }
  if (error || !payload) {
    return (
      <Shell>
        <ErrorCard
          title="Boeking niet gevonden"
          body={error ?? 'Probeer de link uit je e-mail opnieuw.'}
        />
      </Shell>
    );
  }

  if (mode === 'reschedule') {
    return (
      <RescheduleView
        token={token!}
        payload={payload}
        onCancel={() => setMode('view')}
        onSuccess={() => {
          setMode('view');
          void reload();
        }}
      />
    );
  }

  if (mode === 'pay') {
    return (
      <PayView
        token={token!}
        onBack={() => setMode('view')}
        onPaid={(status) => {
          settlePolls.current = 0;
          setPayReturn(status);
          setMode('view');
          void reload();
        }}
      />
    );
  }

  return (
    <Shell>
      {payReturn ? (
        <PaymentReturnBanner
          status={payReturn}
          stillDue={payload.payment?.payable === true}
        />
      ) : null}
      {payload.payment?.payable ? (
        <PaymentDueCard payload={payload} onPay={() => setMode('pay')} />
      ) : null}
      <BookingSummary
        payload={payload}
        token={token!}
        onReschedule={() => setMode('reschedule')}
      />
      <Actions payload={payload} />
    </Shell>
  );
}

// Banner boven de samenvatting na een betaalpoging. Bij succeeded terwijl de
// balans nog open lijkt: de webhook is nog bezig — de poll in App ververst
// de payload vanzelf.
function PaymentReturnBanner({
  status,
  stillDue,
}: {
  status: Exclude<PayReturn, null>;
  stillDue: boolean;
}) {
  if (status === 'failed') {
    return (
      <div className="card error-card small">
        De betaling is niet gelukt of geannuleerd. Je kunt het hieronder
        opnieuw proberen.
      </div>
    );
  }
  if (status === 'processing' || stillDue) {
    return (
      <div className="card pay-banner pay-banner-processing">
        Je betaling wordt verwerkt — dit kan even duren. Deze pagina wordt
        automatisch bijgewerkt.
      </div>
    );
  }
  return (
    <div className="card pay-banner pay-banner-ok">
      Betaling gelukt — je boeking is volledig betaald. Bedankt!
    </div>
  );
}

// "Nog te betalen" kaart met de betaal-CTA. Alleen zichtbaar als de parent
// booking op 'unpaid' staat en er een bedrag open staat (payment.payable).
function PaymentDueCard({
  payload,
  onPay,
}: {
  payload: ManagePayload;
  onPay: () => void;
}) {
  const due = payload.payment!.amount_due_cents;
  return (
    <section className="card pay-due-card">
      <h2>Nog te betalen</h2>
      <p className="pay-due-amount">{formatMoney(due, payload.location.currency)}</p>
      <p className="muted small">
        Deze boeking is nog niet betaald. Betaal eenvoudig online, of bij
        aankomst aan de balie.
      </p>
      <div className="action-row">
        <button className="btn-primary" onClick={onPay}>
          Betaal online
        </button>
      </div>
    </section>
  );
}

// Betaalscherm: haalt (of hervat) de PaymentIntent via start_balance_payment
// en mount het Stripe Payment Element. Redirect-methodes (iDEAL) keren terug
// op deze pagina met ?redirect_status=...; kaarten settelen inline.
function PayView({
  token,
  onBack,
  onPaid,
}: {
  token: string;
  onBack: () => void;
  onPaid: (status: Exclude<PayReturn, null>) => void;
}) {
  const [session, setSession] = useState<BalancePaymentSession | null>(null);
  const [stripePromise, setStripePromise] =
    useState<Promise<StripeJs | null> | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    startBalancePayment(token).then((res) => {
      if (cancelled) return;
      if (res.ok) {
        setSession(res.data);
        setStripePromise(loadStripe(res.data.publishable_key));
      } else if (res.error.error === 'nothing_due' || res.error.error === 'not_payable') {
        setErr(
          'Er staat geen bedrag (meer) open voor deze boeking. Mogelijk is de betaling al verwerkt.',
        );
      } else if (res.error.error === 'payment_in_progress') {
        setErr(
          'Er wordt al een betaling verwerkt voor deze boeking. Vernieuw de pagina over een minuut.',
        );
      } else {
        setErr(res.error.message ?? res.error.error ?? 'Onbekende fout');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <Shell>
      <section className="card">
        <h2>Online betalen</h2>
        {session ? (
          <p className="pay-due-amount">
            {formatMoney(session.amount_due_cents, session.currency)}
          </p>
        ) : null}
        {err ? <div className="error-card small">{err}</div> : null}
        {!session && !err ? <p className="muted">Betaling voorbereiden…</p> : null}
        {session && stripePromise ? (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret: session.client_secret,
              appearance: { theme: 'stripe' },
            }}
          >
            <PayForm token={token} session={session} onPaid={onPaid} />
          </Elements>
        ) : null}
        <div className="action-row">
          <button className="btn-secondary" onClick={onBack}>
            Terug
          </button>
        </div>
      </section>
    </Shell>
  );
}

function PayForm({
  token,
  session,
  onPaid,
}: {
  token: string;
  session: BalancePaymentSession;
  onPaid: (status: Exclude<PayReturn, null>) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setErr(null);

    // Redirect-methodes keren hier terug; Stripe plakt zelf
    // redirect_status (+ payment_intent params) achter de URL.
    const returnUrl = `${window.location.origin}${window.location.pathname}?t=${
      encodeURIComponent(token)
    }`;

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    });

    if (error) {
      setErr(error.message ?? 'Betaling mislukt. Probeer het opnieuw.');
      setBusy(false);
      return;
    }
    if (paymentIntent?.status === 'succeeded') {
      onPaid('succeeded');
      return;
    }
    if (paymentIntent?.status === 'processing') {
      onPaid('processing');
      return;
    }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="pay-form">
      <PaymentElement
        options={{
          layout: {
            type: 'accordion',
            defaultCollapsed: false,
            radios: true,
            spacedAccordionItems: false,
          },
        }}
      />
      {err ? <div className="error-card small">{err}</div> : null}
      <div className="action-row">
        <button
          className="btn-primary"
          type="submit"
          disabled={!stripe || !elements || busy}
        >
          {busy
            ? 'Verwerken…'
            : `Betaal ${formatMoney(session.amount_due_cents, session.currency)}`}
        </button>
      </div>
    </form>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="manage-shell">
      <header className="manage-head">
        <h1>NEOSEUM</h1>
        <p className="muted">Boeking beheren</p>
      </header>
      <main className="manage-main">{children}</main>
      <footer className="manage-foot muted">
        Vragen? <a href="mailto:bookings@neoseum.com">bookings@neoseum.com</a>
      </footer>
    </div>
  );
}

function ErrorCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="card error-card">
      <h2>{title}</h2>
      <p>{body}</p>
    </div>
  );
}

// Recovery entry when the page is opened without a ?t= token: look the
// booking up by number + email (the pair on the Stripe receipt), so a
// customer whose confirmation email never arrived can still reach their
// ticket, downloads, and the resend button.
function LookupView({ onFound }: { onFound: (p: ManagePayload) => void }) {
  const [number, setNumber] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const canSubmit = number.trim().length > 0 && email.trim().length > 3 && !busy;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    const res = await fetchBookingByNumber(number.trim(), email.trim());
    setBusy(false);
    if (res.ok) {
      onFound(res.data);
    } else if (res.status === 404) {
      setErr(
        'Geen boeking gevonden met deze combinatie van boekingsnummer en e-mailadres. Controleer beide en probeer opnieuw.',
      );
    } else {
      setErr(res.error.message ?? res.error.error ?? 'Onbekende fout');
    }
  };

  return (
    <section className="card">
      <h2>Vind je boeking</h2>
      <p className="muted small">
        Geen bevestigingsmail ontvangen of de link kwijt? Vul je boekingsnummer
        in (bijv. <strong>EHV-XXXXXX</strong> — je vindt het op je
        betaalbevestiging van Stripe) plus het e-mailadres van de boeking.
      </p>
      <form onSubmit={submit}>
        <label className="text-label">
          <span>Boekingsnummer</span>
          <input
            type="text"
            value={number}
            onChange={(e) => setNumber(e.target.value.toUpperCase())}
            placeholder="EHV-XXXXXX"
            autoComplete="off"
          />
        </label>
        <label className="text-label">
          <span>E-mailadres</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
        </label>
        {err ? <div className="error-card small">{err}</div> : null}
        <div className="action-row">
          <button className="btn-primary" type="submit" disabled={!canSubmit}>
            {busy ? 'Zoeken…' : 'Boeking opzoeken'}
          </button>
        </div>
      </form>
    </section>
  );
}

function BookingSummary({
  payload,
  token,
  onReschedule,
}: {
  payload: ManagePayload;
  token: string;
  onReschedule: () => void;
}) {
  const { booking, customer, experience, experiences, location, slots } = payload;
  const isCancelled = booking.lifecycle_status === 'cancelled';
  const isArrived   = booking.lifecycle_status === 'arrived';
  const fullName = customer
    ? [customer.first_name, customer.last_name].filter(Boolean).join(' ')
    : '';

  // Re-send confirmation email state. The server enforces the cooldown
  // (429 resend_cooldown), so this is pure presentation.
  const [resend, setResend] = useState<'idle' | 'busy' | 'sent' | 'cooldown'>(
    'idle',
  );
  const handleResend = async () => {
    setResend('busy');
    const res = await resendTicketEmail(token);
    if (res.ok) setResend('sent');
    else if (res.status === 429) setResend('cooldown');
    else setResend('idle');
  };

  // Multi-pick departures (cart-era) render a stacked list of
  // experience-picks under one orderline. Single-pick keeps the legacy
  // "one experience" title.
  const picks = experiences && experiences.length > 0 ? experiences : null;
  const isMultiPick = picks !== null && picks.length > 1;
  const title = isMultiPick
    ? picks!.map((p) => `${p.party_size}× ${p.name}`).join(' + ')
    : experience.name;

  return (
    <section className="card">
      <h2>{title}</h2>
      <div className="status-row">
        <span className={`pill pill-lc-${booking.lifecycle_status}`}>
          {lifecycleLabel(booking.lifecycle_status)}
        </span>
        <span className={`pill pill-pay-${booking.payment_status}`}>
          {paymentLabel(booking.payment_status)}
        </span>
      </div>

      {/* Ticket downloads — the confirmation email no longer attaches the
          PDF/.ics (attachments hurt spam placement), so this page is the
          canonical place to fetch them. Confirmed departures only: the
          endpoint refuses drafts/cancelled anyway. */}
      {booking.lifecycle_status === 'confirmed' ? (
        <>
          <div className="action-row">
            <a className="btn-primary" href={ticketPdfUrl(token)}>
              Download ticket (PDF)
            </a>
            <a className="btn-secondary" href={ticketIcsUrl(token)}>
              Zet in je agenda (.ics)
            </a>
            <button
              className="btn-secondary"
              onClick={handleResend}
              disabled={resend === 'busy' || resend === 'sent'}
            >
              {resend === 'busy' ? 'Versturen…' : 'E-mail opnieuw versturen'}
            </button>
          </div>
          {resend === 'sent' ? (
            <p className="muted small">
              Bevestigingsmail opnieuw verstuurd naar {customer?.email ?? 'je e-mailadres'} —
              kijk ook in je spam- of reclamefolder.
            </p>
          ) : null}
          {resend === 'cooldown' ? (
            <p className="muted small">
              Er is zojuist al een e-mail verstuurd — kijk in je inbox én je
              spam-folder, en probeer het anders over een paar minuten opnieuw.
            </p>
          ) : null}
        </>
      ) : null}

      {/* Primary CTA pulled to the top of the summary — customers were missing
          the reschedule button when it sat below the full booking details. */}
      {rescheduleState(payload) === 'ok' ? (
        <div className="action-row">
          <button className="btn-primary" onClick={onReschedule}>
            Datum / tijdslot wijzigen
          </button>
        </div>
      ) : null}

      <dl className="kv">
        <dt>Boekingsnummer</dt><dd>{booking.booking_number}</dd>
        {fullName ? (<><dt>Naam</dt><dd>{fullName}</dd></>) : null}
        {customer?.email ? (<><dt>E-mail</dt><dd>{customer.email}</dd></>) : null}
        <dt>Personen</dt><dd>{booking.party_size}</dd>
        {booking.total_cents != null ? (
          <>
            <dt>Totaal</dt>
            <dd>{formatMoney(booking.total_cents, location.currency)}</dd>
          </>
        ) : null}
        <dt>Locatie</dt>
        <dd>
          {location.name}
          {location.address_line ? (
            <>
              <br />
              {location.address_line}
              {[location.postal_code, location.city].filter(Boolean).length > 0 ? (
                <>
                  <br />
                  {[location.postal_code, location.city].filter(Boolean).join(' ')}
                </>
              ) : null}
              <br />
              <a href={mapsUrlFor(location)!} target="_blank" rel="noreferrer">
                Bekijk op Google Maps
              </a>
            </>
          ) : null}
          {location.phone ? (
            <>
              <br />
              <a href={`tel:${location.phone.replace(/\s+/g, '')}`}>{location.phone}</a>
            </>
          ) : null}
        </dd>
      </dl>

      {/* Multi-pick: surface per-experience breakdown so the customer can
          see which experience each ticket count belongs to. */}
      {isMultiPick ? (
        <>
          <h3>Expedities</h3>
          <ul className="slot-list">
            {picks!.map((p) => (
              <li key={p.id}>
                <strong>{p.name}</strong>{' · '}
                {p.party_size}p
                {p.duration_minutes ? <> · {p.duration_minutes} min</> : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <h3>{slots.length > 1 ? 'Tijdsloten' : 'Tijdslot'}</h3>
      <ul className="slot-list">
        {slots.map((s, i) => (
          <li key={`${s.slot_start}-${s.room_id}-${i}`}>
            <strong>{venueDate(s.slot_start)}</strong>{' · '}
            {venueTime(s.slot_start)}{' · '}
            {s.room_name}{' · '}
            {s.party_size}p
          </li>
        ))}
      </ul>

      {!isCancelled && !isArrived ? (
        <p className="muted small">
          Wijzigen kan tot{' '}
          <strong>{venueDateTime(payload.cutoff_at)}</strong>.
        </p>
      ) : null}
    </section>
  );
}

// Contextual note shown under the booking summary when self-service reschedule
// is unavailable. The reschedule button itself now lives at the top of the
// summary (see BookingSummary), so the 'ok' case renders nothing here.
function Actions({ payload }: { payload: ManagePayload }) {
  switch (rescheduleState(payload)) {
    case 'cancelled':
      return <p className="muted">Deze boeking is geannuleerd.</p>;
    case 'arrived':
      return <p className="muted">Deze boeking is afgerond — geen wijzigingen meer mogelijk.</p>;
    case 'cutoff':
      return (
        <p className="muted">
          De wijzigingstermijn (1 uur voor aanvang) is verstreken. Neem contact
          op via <a href="mailto:bookings@neoseum.com">bookings@neoseum.com</a> als
          je toch iets moet aanpassen.
        </p>
      );
    case 'unsupported':
      // Older payloads (or a future edge case) may still mark a departure as
      // non-reschedulable — keep the email fallback for that.
      return (
        <p className="muted">
          Online wijzigen werkt voor deze boeking nog niet — neem contact op
          via <a href="mailto:bookings@neoseum.com">bookings@neoseum.com</a>.
        </p>
      );
    default:
      return null;
  }
}

// Convert an AvailabilitySlot into the leg array that fn_manage_reschedule
// expects. fits-in-one collapses to one leg with the full party_size; splits
// pass through the classifier-emitted leg breakdown verbatim.
function slotToNewSlots(
  s: AvailabilitySlot,
  partySize: number,
): NewSlotInput[] {
  if (s.category === 'fits-in-one') {
    return [{ room_id: s.room_id, slot_start: s.slot_start, party_size: partySize }];
  }
  if (s.split && s.split.length > 0) {
    return s.split.map((leg) => ({
      room_id: leg.room_id,
      slot_start: leg.slot_start,
      party_size: leg.party_size,
    }));
  }
  // Defensive: split-2/split-3 should always have a `split` array per the
  // 0025 classifier; falling back to fits-in-one shape would over-book.
  return [];
}

function RescheduleView({
  token,
  payload,
  onCancel,
  onSuccess,
}: {
  token: string;
  payload: ManagePayload;
  onCancel: () => void;
  onSuccess: () => void;
}) {
  const partySize = payload.booking.party_size;
  // Multi-pick departures (2+ experiences sharing one slot) move as a unit:
  // the whole departure shifts to the chosen time, keeping each pick's room +
  // party size. They use the reschedule_multi action and only ever land on a
  // single fits-in-one slot (never a split).
  const isMultiPick = (payload.experiences?.length ?? 0) > 1;
  // Existing slots, sorted earliest-first for the "current" summary.
  const currentSlots = useMemo(
    () =>
      [...payload.slots].sort(
        (a, b) => new Date(a.slot_start).getTime() - new Date(b.slot_start).getTime(),
      ),
    [payload.slots],
  );
  const earliestCurrent = currentSlots[0]!;

  const [date, setDate] = useState<string>(() => todayVenueString());
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [pickedKey, setPickedKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const minDate = useMemo(() => {
    // Earliest allowed date: today venue-time. The 1-hour cutoff
    // (booking_manage_cutoff_1h) is enforced by fn_manage_reschedule.
    const t = new Date();
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: VENUE_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(t);
  }, []);

  useEffect(() => {
    // Mobile date pickers can be cleared (×) → empty string. Guard against
    // sending that to the server (PostgREST returns 400 on empty date).
    if (!date) {
      setSlots([]);
      setPickedKey(null);
      setErr(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setSlots([]);
    setPickedKey(null);
    setErr(null);
    fetchAvailability(
      payload.experience.id,
      date,
      partySize,
      payload.booking.locale,
      // Scope to the booking's own location so a reschedule can only stay at
      // the same venue (cross-location is operator-only; the server rejects it).
      payload.location.id,
    )
      .then((rows) => {
        if (cancelled) return;
        // Multi-pick moves keep the same room and never split, so restrict to
        // fits-in-one slots in the booking's current room.
        setSlots(
          rows.filter(
            (s) =>
              s.category !== 'unavailable' &&
              (!isMultiPick ||
                (s.category === 'fits-in-one' && s.room_id === earliestCurrent.room_id)),
          ),
        );
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    date, payload.experience.id, partySize, payload.booking.locale,
    payload.location.id, isMultiPick, earliestCurrent.room_id,
  ]);

  const pickedSlot = useMemo(
    () => slots.find((s) => `${s.room_id}|${s.slot_start}` === pickedKey) ?? null,
    [slots, pickedKey],
  );

  const submit = async () => {
    if (!pickedSlot) return;
    setBusy(true);
    setErr(null);
    // Multi-pick: a time-only move — send just the new slot start. Single-pick:
    // the existing leg-based reschedule (handles fits-in-one + splits).
    const res = isMultiPick
      ? await rescheduleMultiBooking(token, pickedSlot.slot_start)
      : await (async () => {
          const newSlots = slotToNewSlots(pickedSlot, partySize);
          if (newSlots.length === 0) {
            return {
              ok: false as const,
              status: 0,
              error: {
                error: 'client_error',
                message: 'Onverwachte fout: kon de tijdslot-keuze niet verwerken.',
              },
            };
          }
          return rescheduleBooking(token, newSlots);
        })();
    setBusy(false);
    if (res.ok) {
      onSuccess();
    } else {
      setErr(res.error.message ?? res.error.error);
    }
  };

  return (
    <Shell>
      <section className="card">
        <h2>Datum / tijdslot wijzigen</h2>

        {currentSlots.length === 1 ? (
          <p className="muted small">
            Huidig tijdslot:{' '}
            <strong>{venueDate(earliestCurrent.slot_start)}</strong> om{' '}
            <strong>{venueTime(earliestCurrent.slot_start)}</strong>{' '}
            ({earliestCurrent.room_name}).
          </p>
        ) : (
          <div className="muted small">
            <p style={{ marginBottom: 4 }}>
              Huidige tijdsloten op{' '}
              <strong>{venueDate(earliestCurrent.slot_start)}</strong>:
            </p>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {currentSlots.map((s, i) => {
                const expName = (payload.experiences ?? []).find(
                  (p) => p.experience_id === s.experience_id,
                )?.name;
                return (
                  <li key={`${s.slot_start}-${s.room_id}-${i}`}>
                    {venueTime(s.slot_start)} · {s.room_name} · {s.party_size}p
                    {isMultiPick && expName ? ` · ${expName}` : ''}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <p className="muted small" style={{ marginTop: 12 }}>
          De prijs blijft hetzelfde — je verplaatst alleen de datum/tijd.
        </p>

        <label className="text-label">
          <span>Nieuwe datum</span>
          <input
            type="date"
            value={date}
            min={minDate}
            onChange={(e) => setDate(e.target.value)}
          />
        </label>

        {!date ? (
          <p className="muted">Kies een datum om beschikbare tijden te zien.</p>
        ) : null}
        {date && loading ? <p className="muted">Beschikbaarheid laden…</p> : null}
        {date && !loading && slots.length === 0 ? (
          <p className="muted">Geen beschikbare tijden op deze datum. Probeer een andere dag.</p>
        ) : null}

        {slots.length > 0 ? (
          <ul className="slot-grid">
            {slots.map((s) => {
              const k = `${s.room_id}|${s.slot_start}`;
              const sameDay = venueDayString(s.slot_start) === venueDayString(earliestCurrent.slot_start);
              return (
                <li key={k}>
                  <button
                    type="button"
                    className={`slot-btn ${pickedKey === k ? 'slot-btn-active' : ''}`}
                    onClick={() => setPickedKey(k)}
                  >
                    <strong>
                      {s.category === 'fits-in-one'
                        ? venueTime(s.slot_start)
                        : (s.split ?? []).map((leg) => venueTime(leg.slot_start)).join(' + ')}
                    </strong>
                    <span className="slot-room">{s.room_name}</span>
                    <span className="slot-remaining muted">
                      {s.category === 'fits-in-one'
                        ? `${s.remaining} ${s.remaining === 1 ? 'plek' : 'plekken'} vrij`
                        : `Verdeeld: ${(s.split ?? []).map((leg) => leg.party_size).join('+')} personen`}
                      {sameDay ? ' (zelfde dag)' : ''}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}

        {err ? <div className="error-card small">{err}</div> : null}

        <div className="action-row">
          <button className="btn-secondary" onClick={onCancel} disabled={busy}>Terug</button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || !pickedSlot}
          >
            {busy ? 'Verplaatsen…' : 'Verplaats boeking'}
          </button>
        </div>
      </section>
    </Shell>
  );
}

function lifecycleLabel(lc: ManagePayload['booking']['lifecycle_status']): string {
  switch (lc) {
    case 'draft':     return 'Concept';
    case 'confirmed': return 'Bevestigd';
    case 'arrived':   return 'Aangekomen';
    case 'no_show':   return 'No-show';
    case 'cancelled': return 'Geannuleerd';
    default:          return lc;
  }
}

function paymentLabel(p: ManagePayload['booking']['payment_status']): string {
  switch (p) {
    case 'none':     return 'Geen betaling';
    case 'pending':  return 'Betaling in afwachting';
    case 'paid':     return 'Betaald';
    case 'refunded': return 'Terugbetaald';
    case 'failed':   return 'Mislukt';
    case 'unpaid':   return 'Nog niet betaald';
    default:         return p;
  }
}
